import axios from 'axios';
import * as Sentry from '@sentry/vue';
/**
 * Interface for prompt service
 */
export interface IPromptService {
  getPrompts(): Promise<string[]>;
  setEndpoint(endpoint: string): void;
}

/**
 * Default fallback prompts
 */
export const DEFAULT_PROMPTS = [
  'How should society address the growing wealth gap?',
  'What is the role of government in managing job losses due to AI?',
  'Should markets or centralized planning drive the transition to renewable energy?',
  'Is providing a guaranteed income to all citizens a viable solution to economic instability?',
  'How should we regulate massive multinational corporations dominating global markets?'
];

/**
 * Service to fetch debate prompts from the Cloudflare worker
 */
export class PromptService implements IPromptService {
  private endpoint: string;
  private readonly fallbackPrompts: string[];

  constructor(endpoint?: string, fallbackPrompts?: string[]) {
    this.endpoint = endpoint !== undefined ? endpoint : (typeof import.meta !== 'undefined' ? import.meta.env.VITE_CLOUDFLARE_PROMPT_ENDPOINT || '' : '');
    this.fallbackPrompts = fallbackPrompts || DEFAULT_PROMPTS;

    if (!this.endpoint) {
      console.warn('Prompts endpoint not configured - using default prompts');
    }
  }

  /**
   * Set the endpoint for fetching prompts (useful for tests)
   */
  setEndpoint(endpoint: string): void {
    this.endpoint = endpoint;
  }

  /**
   * Fetch prompts from the Cloudflare worker
   * Falls back to default prompts if fetch fails
   */
  async getPrompts(): Promise<string[]> {
    if (!this.endpoint) {
      return this.fallbackPrompts;
    }

    try {
      const response = await axios.get(this.endpoint);
      if (Array.isArray(response.data)) {
        return response.data;
      }
      throw new Error('Invalid prompt format');
    } catch (error) {
      console.error('Failed to fetch prompts:', error);
      Sentry.captureException(error, {
        contexts: { prompt_fetch: { endpoint: this.endpoint } }
      });
      return this.fallbackPrompts;
    }
  }
}

// Export a singleton instance
export const promptService = new PromptService();
